import { IconButton } from '@hover/blueprint';
import type { Scheme } from '@hover/blueprint-theme/dist/declarations/src/theme/foundation/colors';
import { iconShare } from '@hover/icons';
import React from 'react';

import { useHomeownerAppProfileQuery } from 'src/modules/homeownerApp/graphql/generated';
import {
  useCopyShareHandler,
  useHomeownerAppContext,
} from 'src/modules/homeownerApp/hooks';
import { usePartnerCobrandedShareText } from 'src/modules/homeownerApp/hooks/usePartner';
import type { Project as ProjectType } from 'src/shared/graphql/generated';
import { useCreateProjectShareMutation } from 'src/shared/graphql/generated';
import {
  TranslationNamespace,
  useLocation,
  useTranslate,
} from 'src/shared/hooks';
import { useHasValidNativeShare } from 'src/shared/hooks/useNativeShareHandler';
import { Route as Routes } from 'src/shared/route';
import { buttonPressed } from 'src/shared/telemetry';
import { logError } from 'src/shared/telemetry/logger';

export const ProjectShareButton = ({
  disabled,
  id,
  colorScheme,
}: {
  disabled?: boolean;
  id: ProjectType['id'];
  colorScheme?: Scheme;
}) => {
  const { href } = useLocation();
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [createProjectShare] = useCreateProjectShareMutation();
  const handleCopyShare = useCopyShareHandler();
  const { trackingData } = useHomeownerAppContext();
  const { data: profileData } = useHomeownerAppProfileQuery();
  const orgId = profileData?.profile?.user?.orgs[0]?.id;
  const hasValidNativeShare = useHasValidNativeShare();
  const shareText = usePartnerCobrandedShareText();

  const createAndCopyProjectShareUrl = async (projectId: ProjectType['id']) => {
    const { data, errors } = await createProjectShare({
      variables: {
        projectId: String(projectId),
      },
    });

    buttonPressed({
      ...trackingData,
      button_location: 'project header',
      button_text: 'Share',
      primary_cta: false,
      project_id: projectId,
      track_id: 'share-project',
      org_id: orgId,
    });

    if (errors?.length) errors.forEach(error => logError(error));

    if (data?.createProjectShare?.id) {
      const baseUrl = new URL(Routes.HOMEOWNER_APP_SHARE.path, href);
      const shareUrl = `${baseUrl.href}/${data.createProjectShare.id}`;
      if (hasValidNativeShare) {
        await navigator
          .share({ url: shareUrl, text: shareText })
          .catch(shareError => logError(shareError as Error));
      } else {
        void handleCopyShare({ shareUrl });
      }
    }
  };

  return (
    <IconButton
      colorScheme={colorScheme}
      disabled={disabled}
      icon={iconShare}
      label={t('fullscreenImage.share')}
      onClick={() => createAndCopyProjectShareUrl(id)}
      variant={{ base: 'ghost', tablet: 'glass' }}
    />
  );
};
